import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Download, Loader2, ChevronDown } from 'lucide-react';
import { Midi } from '@tonejs/midi';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel } from './ui/dropdown-menu';
import { logEvent } from '../lib/amplitude';
import { useToast } from "./ui/use-toast";
import SynthTransport from './SynthTransport';
import { parseMidi } from '../lib/synth/player';

// Producers route each part to its own plugin, so besides the whole file they can take one
// track on its own. The single-track file keeps the tempo and time signature.
function trackList(buffer) {
  try {
    return new Midi(buffer).tracks
      // LilyPond writes "Staff name:" (and ":1" for a second voice) as the track name
      .map((t, index) => ({ index, name: (t.name || '').replace(/:\d*$/, '').trim() || (t.instrument?.percussion ? 'Drums' : `Track ${index + 1}`), notes: t.notes.length }))
      .filter((t) => t.notes > 0);
  } catch (e) {
    return [];
  }
}

function singleTrackMidi(buffer, index) {
  const source = new Midi(buffer);
  const out = new Midi();
  out.fromJSON({ header: source.header.toJSON(), tracks: [source.tracks[index].toJSON()] });
  return new Blob([out.toArray()], { type: 'audio/midi' });
}

const fileSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'track';

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Electronic pieces play through our Web Audio engine (src/lib/synth); acoustic ones
// (rock, funk, orchestral, piano/guitar-led) through the sampled GM soundfont, which has
// real instruments for those. localStorage 'midimate.sounds' = 'synth' | 'classic' forces one.
const SOUNDS_KEY = 'midimate.sounds';
function chooseEngine(buffer) {
  try {
    const forced = localStorage.getItem(SOUNDS_KEY);
    if (forced === 'synth' || forced === 'classic') return forced;
  } catch (e) { /* private mode */ }
  try {
    return parseMidi(buffer).style === 'acoustic' ? 'classic' : 'synth';
  } catch (e) {
    return 'classic';
  }
}

// onBeforeDownload may return false (or a promise of false) to stop a download: the first
// one is free, the rest sit behind a pack or a plan
function MidiPlayer({ filePath, onDownloaded, onBeforeDownload }) {
  const { toast } = useToast();
  const playerRef = useRef(null);
  const visualizerRef = useRef(null);
  const [isToneLoaded, setIsToneLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [midi, setMidi] = useState(null);          // { url, buffer, engine }
  const useSynth = midi?.engine === 'synth';
  const tracks = useMemo(() => (midi ? trackList(midi.buffer) : []), [midi]);

  const handleTrackDownload = async (track) => {
    logEvent('MIDI Track Download', { filePath, track: track.name, tracks: tracks.length });
    if (onBeforeDownload && !(await onBeforeDownload())) return;
    try {
      saveBlob(singleTrackMidi(midi.buffer, track.index), `midimate-${fileSlug(track.name)}.mid`);
      if (onDownloaded) onDownloaded();
      toast({
        title: `${track.name} downloaded`,
        description: "Just that part, with the tempo kept. Drag it onto the instrument it's for.",
        duration: 3000,
      });
    } catch (error) {
      console.error('Error building track file:', error);
      logEvent('MIDI Download Error', { filePath, track: track.name, error: error.message });
      toast({ title: "Download Failed", description: "Could not split that track out. Try the full file.", variant: "destructive", duration: 3000 });
    }
  };

  useEffect(() => {
    const checkTone = () => {
      if (window.Tone) {
        setIsToneLoaded(true);
      } else {
        setTimeout(checkTone, 100);
      }
    };
    checkTone();
  }, []);

  useEffect(() => {
    if (!isToneLoaded || !filePath) return;
    setIsLoading(true);
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.storage.from('midi-files').download(filePath);
        if (error) throw error;
        const buffer = await data.arrayBuffer();
        if (cancelled) return;
        setMidi({ url: URL.createObjectURL(data), buffer, engine: chooseEngine(buffer) });
      } catch (error) {
        console.error('Error fetching MIDI file:', error);
        setIsLoading(false);
        logEvent('MIDI Player Error', { filePath, error: error.message });
      }
    })();
    return () => { cancelled = true; };
  }, [filePath, isToneLoaded]);

  // Attach the file once the chosen player element is mounted
  useEffect(() => {
    if (!midi || !visualizerRef.current) return;
    if (midi.engine === 'synth') {
      visualizerRef.current.src = midi.url;
      setIsLoading(false);
      logEvent('MIDI Player Loaded', { filePath, engine: 'synth' });
      return;
    }
    const player = playerRef.current;
    if (!player) return;
    player.src = midi.url;
    player.addVisualizer(visualizerRef.current);
    const loaded = () => { setIsLoading(false); logEvent('MIDI Player Loaded', { filePath, engine: 'classic' }); };
    if (player.isLoaded) loaded(); else player.addEventListener('load', loaded, { once: true });
    player.addEventListener('start', () => logEvent('MIDI Playback Started', { filePath, engine: 'classic' }));
    player.addEventListener('pause', () => logEvent('MIDI Playback Paused', { filePath, engine: 'classic' }));
    player.addEventListener('stop', () => logEvent('MIDI Playback Completed', { filePath, engine: 'classic' }));
  }, [midi, filePath]);

  const handleDownload = async () => {
    setIsDownloading(true);
    logEvent('MIDI Download Initiated', { filePath });
    if (onBeforeDownload && !(await onBeforeDownload())) {
      setIsDownloading(false);
      return;
    }
    try {
      const { data, error } = await supabase.storage
        .from('midi-files')
        .download(filePath);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'midimate.mid';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      logEvent('MIDI Download Completed', { filePath });
      if (onDownloaded) onDownloaded();
      
      toast({
        title: "Download Successful",
        description: "Your MIDI file has been downloaded. Drag it into your DAW to import it into your project!",
        duration: 3000,
      });
    } catch (error) {
      console.error('Error downloading MIDI file:', error);
      logEvent('MIDI Download Error', { filePath, error: error.message });
      
      toast({
        title: "Download Failed",
        description: "There was an error downloading your MIDI file. Please try again.",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  if (!isToneLoaded) {
    return null; // Or you could return a "Loading Tone.js..." message
  }

  return (
    <Card className="w-full mt-2">
      <CardContent className="p-2">
        <div className="midi-player-container space-y-2 relative">
          <div className="overflow-hidden w-full">
            {useSynth ? (
              <SynthTransport
                buffer={midi.buffer}
                visualizerRef={visualizerRef}
                onStart={() => logEvent('MIDI Playback Started', { filePath, engine: 'synth' })}
                onPause={() => logEvent('MIDI Playback Paused', { filePath, engine: 'synth' })}
                onEnd={() => logEvent('MIDI Playback Completed', { filePath, engine: 'synth' })}
              />
            ) : (
            <midi-player
              ref={playerRef}
              sound-font="https://storage.googleapis.com/magentadata/js/soundfonts/sgm_plus"
              className="w-full"
              style={{
                minWidth: '0',
                width: '100%',
                fontSize: '12px',
                '--player-height': '40px',
                '--button-width': '40px',
                '--button-height': '40px',
                '--seek-width': 'calc(100% - 80px)',
                '--seek-height': '4px',
                '--seek-thumb-width': '12px',
                '--seek-thumb-height': '12px',
              }}
            ></midi-player>
            )}
          </div>
          <div className="visualizer-container overflow-y-auto max-w-full" style={{ maxHeight: '300px' }}> {/* Changed to overflow-y-auto and use inline style for max-height */}
            <midi-visualizer 
              ref={visualizerRef}
              type="piano-roll"
              className="w-full h-full"
              style={{
                minWidth: '0',
                width: '100%',
                fontSize: '8px',
                overflowX: 'hidden',
              }}
            ></midi-visualizer>
          </div>
          {isLoading && (
            <div className="absolute inset-0 flex justify-center items-center bg-background/50 z-10">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              Works in Ableton, FL Studio, Logic and any DAW.
            </p>
            <div className="flex w-full sm:w-auto gap-1">
              <Button
                onClick={handleDownload}
                className="text-xs py-1 px-4 flex-1 sm:flex-none bg-[#f05477] hover:bg-[#d03357] text-white"
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <>
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="mr-1 h-4 w-4" /> Download MIDI
                  </>
                )}
              </Button>
              {tracks.length > 1 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="text-xs py-1 px-2" aria-label="Download one track">
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Download one track</DropdownMenuLabel>
                    {tracks.map((track) => (
                      <DropdownMenuItem key={track.index} onClick={() => handleTrackDownload(track)}>
                        <Download className="mr-2 h-3.5 w-3.5" /> {track.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default MidiPlayer;
